import { ActivityHero } from "../activity-hero/ActivityHero";
import { Volume2, Ellipsis, MicOff, Mic } from "lucide-react";
import { ArrowLeft, Lightbulb, Play, RotateCcw, Star, Wifi, WifiOff } from "lucide-react";
import { useState, useCallback, useRef, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { useQuizSession } from "../../hooks/use-quiz-session";
import { AudioRecorder } from "../../lib/audio";
import type { RewardRecognition } from "../../../shared/live-proxy-protocol";
import "./quiz-widget.scss";

// ── Audio beep for tap-to-talk ──

let _beepCtx: AudioContext | null = null;

function playBeep(ascending: boolean) {
  try {
    if (!_beepCtx) _beepCtx = new AudioContext();
    const ctx = _beepCtx;
    const doPlay = () => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      const now = ctx.currentTime;
      if (ascending) {
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.linearRampToValueAtTime(600, now + 0.1);
      } else {
        osc.frequency.setValueAtTime(600, now);
        osc.frequency.linearRampToValueAtTime(400, now + 0.1);
      }
      gain.gain.setValueAtTime(0.12, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.12);
      osc.start(now);
      osc.stop(now + 0.12);
    };
    // On iOS, AudioContext may be suspended after backgrounding (or in the
    // non-standard "interrupted" state after a permission dialog) — resume first.
    if ((ctx.state as string) !== "running") {
      ctx.resume().then(doPlay).catch(() => {});
    } else {
      doPlay();
    }
  } catch { /* ignore */ }
}

type MicPermissionState = "unknown" | "prompt" | "prompting" | "granted" | "denied" | "unsupported";
type MicIssueKind = "blocked" | "system-blocked" | "not-found" | "busy" | "unsupported" | "unknown";

type MicIssue = {
  kind: MicIssueKind;
  errorName?: string;
};

type MicDisplay = "hidden" | "idle" | "permission" | "blocked" | "recording" | "agent-speaking" | "processing";

const permissionDescriptor = { name: "microphone" } as PermissionDescriptor;

function hasGetUserMedia(): boolean {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
}

function createMicError(name: string, message: string): Error {
  try {
    return new DOMException(message, name);
  } catch {
    const error = new Error(message);
    error.name = name;
    return error;
  }
}

function classifyMicFailure(error: unknown, permissionState: MicPermissionState): MicIssue {
  const name = error instanceof DOMException || error instanceof Error ? error.name : "";

  if (!hasGetUserMedia() || name === "NotSupportedError") {
    return { kind: "unsupported", errorName: name };
  }

  if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") {
    return { kind: "not-found", errorName: name };
  }

  if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") {
    return { kind: "busy", errorName: name };
  }

  if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") {
    return {
      kind: permissionState === "granted" ? "system-blocked" : "blocked",
      errorName: name,
    };
  }

  return { kind: "unknown", errorName: name };
}

export type QuizWidgetProps = {
  personaProfileId?: string;
  onAdvanced?: (newStep: number) => void;
};

// ── Component ──

export default function QuizWidget({ personaProfileId, onAdvanced }: QuizWidgetProps) {
  const { status, unlockAudio, client } = useLiveAPIContext();
  const { session, startQuestion, sendChip, sendAnswerChoice } = useQuizSession({ onAdvanced });

  const { phase, question: q, answered, correctAnswer, hebrewExplanation,
          flashcardBack, flashcardExample, canAdvance, error, isAgentSpeaking,
          recognition } = session;

  // ── Local audio UI state ──
  const [audioRecorder] = useState(() => new AudioRecorder());
  const [micMode, setMicMode] = useState<"off" | "recording">("off");
  const [waitingForReply, setWaitingForReply] = useState(false);
  // The agent never replied to a recorded answer — prompt the child to try again
  const [noReply, setNoReply] = useState(false);
  const [micPermissionState, setMicPermissionState] = useState<MicPermissionState>("unknown");
  const [micIssue, setMicIssue] = useState<MicIssue | null>(null);
  const mountedRef = useRef(false);
  const statusRef = useRef(status);
  const micPermissionStateRef = useRef(micPermissionState);
  // Last state reported by permissions.query — kept separately so Sentry tags
  // reflect what the browser said, not the transient UI flow state.
  const micQueriedStateRef = useRef<string>("unknown");
  const agentSpeakingRef = useRef(false);
  statusRef.current = status;
  micPermissionStateRef.current = micPermissionState;
  agentSpeakingRef.current = isAgentSpeaking;

  const lastSpeechRef = useRef(0);
  const hasSpokenRef  = useRef(false);

  // ── Transient UI animations ──
  const [sentChip, setSentChip] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [starBump, setStarBump] = useState(false);
  const [visibleRecognition, setVisibleRecognition] = useState<RewardRecognition | null>(null);

  const currentStep = session.rewards?.currentStep ?? null;
  const totalStars = session.rewards?.totalStars ?? null;
  const prevStepRef = useRef<number | null>(currentStep);
  const prevStarsRef = useRef<number | null>(totalStars);

  // Depend on primitive values so a same-value rewards refresh cannot cancel
  // an active timer and leave its animation stuck on screen.
  useEffect(() => {
    if (totalStars === null) return;
    if (prevStarsRef.current === null) {
      prevStarsRef.current = totalStars;
      return;
    }
    const increased = totalStars > prevStarsRef.current;
    prevStarsRef.current = totalStars;
    if (!increased) return;
    setStarBump(true);
    const timer = setTimeout(() => setStarBump(false), 600);
    return () => clearTimeout(timer);
  }, [totalStars]);

  useEffect(() => {
    if (currentStep === null) return;
    if (prevStepRef.current === null) {
      prevStepRef.current = currentStep;
      return;
    }
    const increased = currentStep > prevStepRef.current;
    prevStepRef.current = currentStep;
    if (!increased) return;
    setShowCelebration(true);
    const timer = setTimeout(() => setShowCelebration(false), 3000);
    return () => clearTimeout(timer);
  }, [currentStep]);

  const recognitionType = recognition?.type ?? null;
  const recognitionValue = recognition?.value ?? null;
  useEffect(() => {
    if (!recognitionType || recognitionValue === null) {
      setVisibleRecognition(null);
      return;
    }
    setVisibleRecognition({ type: recognitionType, value: recognitionValue });
    const timer = setTimeout(() => setVisibleRecognition(null), 2000);
    return () => clearTimeout(timer);
  }, [recognitionType, recognitionValue]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => { return () => { audioRecorder.stop(); }; }, [audioRecorder]);

  useEffect(() => {
    if (!hasGetUserMedia()) {
      setMicPermissionState("unsupported");
      setMicIssue({ kind: "unsupported" });
      return;
    }

    if (!navigator.permissions?.query) {
      setMicPermissionState("unknown");
      return;
    }

    let cancelled = false;
    let permissionStatus: PermissionStatus | null = null;
    let handleChange: (() => void) | null = null;

    const applyPermissionState = (state: PermissionState) => {
      if (cancelled) return;
      micQueriedStateRef.current = state;
      Sentry.addBreadcrumb({ category: "mic-permission", message: `permissions.query: ${state}` });
      setMicPermissionState(state === "granted" ? "granted" : state === "denied" ? "denied" : "prompt");
      setMicIssue((current) => {
        if (state === "denied") {
          return current?.kind === "system-blocked" ? current : { kind: "blocked", errorName: "NotAllowedError" };
        }
        return current?.kind === "blocked" ? null : current;
      });
    };

    navigator.permissions
      .query(permissionDescriptor)
      .then((result) => {
        if (cancelled) return;
        permissionStatus = result;
        applyPermissionState(result.state);
        handleChange = () => applyPermissionState(result.state);
        result.addEventListener("change", handleChange);
      })
      .catch(() => {
        if (cancelled) return;
        Sentry.addBreadcrumb({ category: "mic-permission", message: "permissions.query: unsupported" });
        setMicPermissionState("unknown");
      });

    return () => {
      cancelled = true;
      if (permissionStatus && handleChange) {
        permissionStatus.removeEventListener("change", handleChange);
      }
    };
  }, []);

  // Stop recording when agent starts speaking
  useEffect(() => {
    if (isAgentSpeaking && micMode === "recording") {
      playBeep(false);
      setMicMode("off");
    }
    if (isAgentSpeaking) setWaitingForReply(false);
  }, [isAgentSpeaking, micMode]);

  // Stop recording when WS permanently disconnects
  useEffect(() => {
    if (status === "disconnected") {
      setMicMode("off");
      setWaitingForReply(false);
      setMicPermissionState((current) => current === "prompting" ? "prompt" : current);
    }
  }, [status]);

  // Safety timeout: if agent never responds, prompt the child to try again
  useEffect(() => {
    if (!waitingForReply) return;
    const t = setTimeout(() => {
      setWaitingForReply(false);
      setNoReply(true);
    }, 12_000);
    return () => clearTimeout(t);
  }, [waitingForReply]);

  // Clear the try-again prompt once the agent replies or a new question starts
  useEffect(() => {
    if (isAgentSpeaking || phase !== "ready") setNoReply(false);
  }, [isAgentSpeaking, phase]);

  // Tap-to-talk: relay mic audio through the proxy
  useEffect(() => {
    if (micMode !== "recording" || status !== "connected") {
      audioRecorder.stop();
      document.documentElement.style.setProperty("--mic-scale", "1");
      document.documentElement.style.setProperty("--mic-volume", "0");
      return;
    }

    client.sendActivityStart();

    const onData = (base64: string) => {
      if (agentSpeakingRef.current) return;
      client.sendAudio(base64, `audio/pcm;rate=${audioRecorder.actualSampleRate}`);
    };

    const onVolume = (vol: number) => {
      document.documentElement.style.setProperty("--mic-scale", `${1 + Math.min(vol * 1.5, 0.15)}`);
      document.documentElement.style.setProperty("--mic-volume", `${Math.min(vol * 8, 1)}`);
      if (vol > 0.015) { lastSpeechRef.current = Date.now(); hasSpokenRef.current = true; }
    };

    audioRecorder
      .on("data", onData)
      .on("volume", onVolume)
      .start()
      .catch((e: unknown) => {
        handleMicAccessFailure(e, "recorder-start");
        playBeep(false);
        setWaitingForReply(false);
        setMicMode("off");
      });

    const maxTimer    = setTimeout(() => { playBeep(false); setWaitingForReply(true); setMicMode("off"); }, 10_000);
    const silenceCheck = setInterval(() => {
      if (hasSpokenRef.current && Date.now() - lastSpeechRef.current > 1500) {
        playBeep(false); setWaitingForReply(true); setMicMode("off");
      }
    }, 250);

    return () => {
      clearTimeout(maxTimer);
      clearInterval(silenceCheck);
      audioRecorder.off("data", onData).off("volume", onVolume);
      client.sendActivityEnd();
    };
  }, [micMode, status, client, audioRecorder]);

  // ── Actions ──

  const handleMicAccessFailure = useCallback((e: unknown, event: string, stateBeforeAttempt?: MicPermissionState) => {
    const permissionState = stateBeforeAttempt ?? micPermissionStateRef.current;
    const issue = classifyMicFailure(e, permissionState);

    // A re-check while the permission is known-denied is guaranteed to fail
    // (Chrome never re-prompts after a hard block) — breadcrumb, don't report.
    const knownBlocked = permissionState === "denied" && (issue.kind === "blocked" || issue.kind === "system-blocked");
    if (knownBlocked) {
      Sentry.addBreadcrumb({ category: "mic-permission", message: `re-check failed: ${issue.errorName ?? "unknown"}`, level: "warning" });
    } else {
      Sentry.captureException(e, { tags: { flow: "audio", event, micPermissionState: permissionState, micPermissionQueried: micQueriedStateRef.current } });
    }

    setMicIssue(issue);
    setWaitingForReply(false);
    setMicMode("off");

    if (issue.kind === "unsupported") {
      setMicPermissionState("unsupported");
    } else if (issue.kind === "blocked" || issue.kind === "system-blocked") {
      setMicPermissionState("denied");
    } else {
      setMicPermissionState((current) => current === "prompting" ? "unknown" : current);
    }
  }, []);

  const beginMicCapture = useCallback(() => {
    const stateBeforeAttempt = micPermissionStateRef.current;
    if (stateBeforeAttempt === "prompting") return;

    if (!hasGetUserMedia()) {
      handleMicAccessFailure(
        createMicError("NotSupportedError", "Microphone capture is not supported in this browser context."),
        "recorder-prepare",
      );
      return;
    }

    unlockAudio();
    setMicIssue(null);
    setWaitingForReply(false);
    setNoReply(false);
    hasSpokenRef.current = false;
    lastSpeechRef.current = Date.now();
    setMicPermissionState((current) => current === "granted" ? "granted" : "prompting");

    audioRecorder
      .prepare()
      .then(() => {
        if (!mountedRef.current) return;
        setMicIssue(null);
        setMicPermissionState("granted");

        if (statusRef.current !== "connected" || agentSpeakingRef.current) {
          audioRecorder.stop();
          return;
        }

        playBeep(true);
        setMicMode("recording");
      })
      .catch((e: unknown) => {
        if (!mountedRef.current) return;
        playBeep(false);
        handleMicAccessFailure(e, "recorder-prepare", stateBeforeAttempt);
      });
  }, [audioRecorder, handleMicAccessFailure, unlockAudio]);

  const handleStartQuestion = useCallback(() => {
    unlockAudio(); // must run inside the tap gesture so iOS allows playback
    setMicMode("off");
    setWaitingForReply(false);
    hasSpokenRef.current = false;
    startQuestion(personaProfileId ?? "default");
  }, [startQuestion, personaProfileId, unlockAudio]);

  // ── Render ──

  const isFlashcardAnswered = q?.questionType === "flashcard" && phase === "answered";
  const isActive = (phase === "ready" || phase === "answered") && (status === "connected" || status === "reconnecting");

  let micDisplay: MicDisplay = "hidden";
  if (isActive) {
    micDisplay = status === "reconnecting"  ? "processing"
      : isAgentSpeaking                     ? "agent-speaking"
      : micPermissionState === "prompting"  ? "permission"
      : micPermissionState === "denied"     ? "blocked"
      : micMode === "recording"             ? "recording"
      : waitingForReply                     ? "processing"
      : "idle";
  }
  const disableQuickReplies = isAgentSpeaking || waitingForReply || micPermissionState === "prompting";

  return (
    <div className={`quiz-widget${phase === "idle" && !error ? " quiz-widget--entry" : ""}`} dir="rtl">
      {/* Error */}
      {error && (
        <div className="error-card" role="alert">
          <WifiOff className="brand-icon"  style={{ fontSize: 40, color: "var(--wrong)" }} aria-hidden="true" strokeWidth={2.4} />
          <p>{error}</p>
          <button onClick={handleStartQuestion}>נסו שוב</button>
        </div>
      )}

      {/* Idle / Start */}
      {phase === "idle" && !error && (
        <div className="practice-welcome activity-entry">
          <ActivityHero />
          <button className="start-btn" onClick={handleStartQuestion}>
            <Play className="brand-icon" aria-hidden="true" strokeWidth={2.4} />
            התחלה!
          </button>
        </div>
      )}

      {/* Loading */}
      {phase === "loading" && !q && (
        <div className="loading-container" role="status" aria-live="polite">
          <div className="loading-dots" aria-hidden="true"><span /><span /><span /></div>
          <span className="loading-text">מכין שאלה...</span>
        </div>
      )}

      {/* Multiple Choice */}
      {q && q.questionType === "multiple_choice" && (
        <div className="quiz-card" role="group" aria-label="שאלת רב-ברירה">
          <p className="quiz-hebrew-hint" dir="rtl">{q.hebrewHint}</p>
          <p className="quiz-question" dir="ltr" id="quiz-question-text">{q.question}</p>
          <div className="options" role="radiogroup" aria-labelledby="quiz-question-text">
            {q.options.map((opt) => (
              <button
                key={opt}
                className={optionClass(opt, answered, correctAnswer)}
                disabled={!!answered}
                dir="ltr"
                role="radio"
                aria-checked={answered === opt}
                onClick={() => { if (!answered) sendAnswerChoice(opt); }}
              >
                {opt}
              </button>
            ))}
          </div>
          {answered && hebrewExplanation && (
            <p className="explanation" dir="rtl" role="status" aria-live="polite">{hebrewExplanation}</p>
          )}
          {answered && correctAnswer && (
            <div className="sr-only" role="status" aria-live="assertive">
              {answered === correctAnswer ? `תשובה נכונה: ${correctAnswer}` : `תשובה שגויה. התשובה הנכונה: ${correctAnswer}`}
            </div>
          )}
        </div>
      )}

      {/* Fill in the Blank */}
      {q && q.questionType === "fill_blank" && (
        <div className="quiz-card" role="group" aria-label="השלמת משפט">
          <p className="quiz-hebrew-hint" dir="rtl">{q.hebrewHint}</p>
          <p className="quiz-question" dir="ltr">{q.sentence}</p>
          <p className="quiz-hebrew-secondary" dir="rtl">{q.hint}</p>
          {answered && answered !== "flip" && (
            <>
              <div className="fill-result" dir="ltr" role="status" aria-live="polite">
                <span className={answered.toLowerCase() === (correctAnswer ?? "").toLowerCase() ? "correct-answer" : "wrong-answer"}>
                  {answered}
                </span>
                {answered.toLowerCase() !== (correctAnswer ?? "").toLowerCase() && (
                  <><span className="arrow" aria-hidden="true">&rarr;</span><span className="correct-answer">{correctAnswer}</span></>
                )}
              </div>
              {hebrewExplanation && <p className="explanation" dir="rtl">{hebrewExplanation}</p>}
            </>
          )}
        </div>
      )}

      {/* Flashcard */}
      {q && q.questionType === "flashcard" && (
        <div
          className={`flashcard ${isFlashcardAnswered ? "flipped" : ""}`}
          role="region"
          aria-label="כרטיסיית לימוד"
          aria-live="polite"
        >
          {!isFlashcardAnswered ? (
            <p className="flashcard-word" dir="ltr">{q.front}</p>
          ) : (
            <>
              <p className="flashcard-word" dir="ltr">{q.front}</p>
              <p className="flashcard-hebrew" dir="rtl">{flashcardBack}</p>
              <p className="flashcard-example" dir="ltr">{flashcardExample}</p>
            </>
          )}
        </div>
      )}

      {/* Quick-reply chips — pre-answer only */}
      {q && phase === "ready" && (status === "connected" || status === "reconnecting") && (
        <div className="quick-chips">
          <button
            className={`chip-btn chip-hint${sentChip === "hint" ? " chip-sent" : ""}`}
            disabled={disableQuickReplies}
            onClick={() => { sendChip("hint"); setSentChip("hint"); setTimeout(() => setSentChip(null), 600); }}
          >
            <Lightbulb className="brand-icon" aria-hidden="true" strokeWidth={2.4} />
            רמז
          </button>
          <button
            className={`chip-btn chip-repeat${sentChip === "repeat" ? " chip-sent" : ""}`}
            disabled={disableQuickReplies}
            onClick={() => { sendChip("repeat"); setSentChip("repeat"); setTimeout(() => setSentChip(null), 600); }}
          >
            <RotateCcw className="brand-icon" aria-hidden="true" strokeWidth={2.4} />
            שוב
          </button>
        </div>
      )}

      {/* Next */}
      {canAdvance && (
        <button className="next-btn" onClick={handleStartQuestion}>
          <span className="next-btn-label">הבא</span>
          <ArrowLeft className="brand-icon next-btn-icon" aria-hidden="true" strokeWidth={2.4} />
        </button>
      )}

      {starBump && <div className="star-pop" aria-hidden="true">&#11088;</div>}

      {visibleRecognition && visibleRecognition.type !== "step" && (
        <div className={`reward-toast ${visibleRecognition.type}`} dir="rtl" role="status" aria-live="polite">
          {recognitionText(visibleRecognition)}
        </div>
      )}

      {showCelebration && (
        <div className="celebration-banner" role="status" aria-live="assertive">
          <Star className="brand-icon celebration-icon"  aria-hidden="true" strokeWidth={2.4} />
          <span className="celebration-text" dir="rtl">כל הכבוד! עולים לשלב {currentStep}</span>
          <Star className="brand-icon celebration-icon"  aria-hidden="true" strokeWidth={2.4} />
        </div>
      )}

      {/* Transient server notice (e.g. reconnecting mid-question) */}
      {session.notice && (
        <div className="notice-toast" dir="rtl" role="status" aria-live="polite">
          <Wifi className="brand-icon"  aria-hidden="true" strokeWidth={2.4} />
          {session.notice}
        </div>
      )}

      {/* Bottom bar with mic */}
      {micDisplay !== "hidden" && (
        <div className={`bottom-bar ${micDisplay}`}>
          {micDisplay === "idle" && (micIssue
            ? <span className="turn-indicator mic-error" dir="rtl" role="alert">אין גישה למיקרופון 🎤 אפשרו מיקרופון בדפדפן ונסו שוב</span>
            : noReply
              ? <span className="turn-indicator no-reply" dir="rtl" role="alert">לא שמענו אתכם 🎤 לחצו ונסו שוב</span>
              : <span className="turn-indicator idle" dir="rtl" role="status">לחצו ודברו!</span>)}
          {micDisplay === "blocked" && (
            <span className="turn-indicator mic-error" dir="rtl" role="alert">
              המיקרופון חסום 🎤 אפשר לענות בלחיצה על התשובות!
              <small className="mic-error-hint">מבוגרים: לחצו על סמל המנעול 🔒 ליד כתובת האתר ואפשרו מיקרופון</small>
            </span>
          )}
          {micDisplay === "permission"    && <span className="turn-indicator permission" dir="rtl" role="status" aria-live="polite">מחכים לאישור מיקרופון...</span>}
          {micDisplay === "recording"      && <span className="turn-indicator recording" dir="rtl" role="status" aria-live="assertive">מקשיבים...</span>}
          {micDisplay === "processing"     && <div className="processing-dots" role="status" aria-label="ממתין לתשובה"><span /><span /><span /></div>}
          {micDisplay === "agent-speaking" && <span className="turn-indicator agent" dir="rtl" role="status" aria-live="polite">המורה מדברת...</span>}
          <div className={`mic-aura ${micDisplay}`}>
            <button
              className={`mic-btn ${micDisplay}`}
              aria-label={
                micDisplay === "agent-speaking" ? "המורה מדברת" :
                micDisplay === "processing" ? "ממתין לתשובה" :
                micDisplay === "permission" ? "ממתין להרשאת מיקרופון" :
                micDisplay === "blocked" ? "המיקרופון חסום, לחצו לבדיקה חוזרת" :
                micDisplay === "recording" ? "לחצו להפסקת הקלטה" :
                micIssue ? "נסו לפתוח הרשאת מיקרופון" : "לחצו לדבר"
              }
              disabled={micDisplay === "agent-speaking" || micDisplay === "processing" || micDisplay === "permission"}
              onClick={() => {
                if (micDisplay === "idle" || micDisplay === "blocked") {
                  beginMicCapture();
                } else if (micDisplay === "recording") {
                  playBeep(false);
                  setWaitingForReply(true);
                  setMicMode("off");
                }
              }}
            >
              {(() => { const Icon = micDisplay === "agent-speaking" ? Volume2 : micDisplay === "processing" || micDisplay === "permission" ? Ellipsis : micDisplay === "blocked" ? MicOff : Mic; return <Icon className="brand-icon" aria-hidden="true" strokeWidth={2.4} />; })()}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function optionClass(opt: string, answered: string | null, correctAnswer: string | null): string {
  if (!answered || !correctAnswer) return "option-btn";
  if (opt === correctAnswer) return "option-btn correct";
  if (opt === answered) return "option-btn wrong";
  return "option-btn dimmed";
}

function recognitionText(recognition: RewardRecognition): string {
  switch (recognition.type) {
    case "stars": return `⭐ הגעת ל־${recognition.value} כוכבים!`;
    case "streak": return `🔥 רצף של ${recognition.value} תשובות נכונות!`;
    case "progress": return `🎯 כבר פתרת ${recognition.value} שאלות!`;
    case "step": return `עלית לשלב ${recognition.value}`;
  }
}
